import { ChartBar, TrendingUp } from "@/components/icons";
import { Skeleton } from "@/components/kit";
import type { DashboardSummaryDTO } from "@/modules/dashboard/domain/types";
import { SalesChart } from "@/modules/reports/presentation/ui/SalesChart";
import { formatProductQuantity } from "@/modules/reports/presentation/lib/format-product-qty";
import { formatLak } from "@/shared/lib/currency";
import { Link } from "@tanstack/react-router";

type DashboardSalesSectionProps = {
  summary?: DashboardSummaryDTO;
  isLoading?: boolean;
};

export function DashboardSalesSection({
  summary,
  isLoading,
}: DashboardSalesSectionProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-48 rounded-lg" />
        <div className="grid gap-4 lg:grid-cols-5">
          <Skeleton className="h-55 rounded-2xl lg:col-span-3" />
          <Skeleton className="h-55 rounded-2xl lg:col-span-2" />
        </div>
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-4" data-tourid="dashboard-sales">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="font-display text-lg">ແນວໂນ້ມຂາຍ</h2>
          <p className="text-muted-foreground text-sm">
            ມື້ນີ້ {formatLak(summary?.salesTodayRevenue ?? "0")} ·{" "}
            {summary?.ordersToday ?? 0} ອໍເດີ້
          </p>
        </div>
        <Link
          to="/app/reports?tab=sales&preset=7d"
          className="inline-flex items-center gap-1.5 font-medium text-secondary text-sm underline-offset-4 hover:underline"
        >
          <ChartBar className="size-4" />
          ລາຍງານເຕັມ
        </Link>
      </div>

      <div className="grid min-w-0 gap-4 lg:grid-cols-5 lg:items-stretch">
        <div className="min-w-0 lg:col-span-3">
          <SalesChart
            rows={summary?.salesByDay7d}
            isLoading={false}
            compact
            title="ຍອດຂາຍ 7 ວັນ"
          />
        </div>

        <div className="brand-card flex min-w-0 flex-col overflow-hidden lg:col-span-2">
          <div className="border-secondary/15 border-b px-4 py-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="size-4 text-secondary" />
              <h3 className="font-display text-base">ສິນຄ້າຂາຍດີ</h3>
            </div>
            <p className="text-muted-foreground text-xs">7 ວັນລ່າສຸດ</p>
          </div>
          {summary?.topProducts7d?.length ? (
            <ul className="flex flex-1 flex-col divide-y divide-secondary/10">
              {summary.topProducts7d.slice(0, 5).map((row) => (
                <li
                  key={row.variantId}
                  className="flex items-start justify-between gap-2 px-4 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-sm">
                      {row.productName}
                    </p>
                    <p className="truncate text-muted-foreground text-xs">
                      {row.variantName}
                    </p>
                  </div>
                  <div className="shrink-0 text-right text-sm">
                    <p className="font-medium tabular-nums">
                      {formatLak(row.revenue)}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {formatProductQuantity(row)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="flex flex-1 items-center justify-center p-4 text-center text-muted-foreground text-sm">
              ບໍ່ມີຂໍ້ມູນ
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
